import React, { useState } from "react";
import MaterialModal from "../../../../app/material-modal";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { inputCommonStyle } from "../../../../../util/admin/cssCommonStyle";
import { useQueryClient } from "@tanstack/react-query";
import { baseAtom } from "../../../../../stores/jotai/base";
import { useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useFormik } from "formik";
import { toast } from "sonner";
import { useAtomValue } from "jotai";
import { useCreateSession } from "../../../../../tanstackQuery/queries/superAdmin/session";

const CreateSessionModal = ({ open, handleClose, cohortList }) => {
  const base=useAtomValue(baseAtom);
  const [cohort, setCohort] = useState("");
  const client = useQueryClient();
  const { orgID } = useParams();
  const { keycloak } = useKeycloak();

  const {
    mutateAsync: createSession,
    isPending,
    isSuccess,
  } = useCreateSession(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["org-invite-sessions"]});
        handleClose();
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      groupID: "",
    },
    validate: (values) => {
      const errors: { name?: string; groupID?: string } = {};
      if (!values.name) {
        errors.name = "required";
      }
      if (!values.groupID) {
        errors.groupID = "required";
      }
      return errors;
    },
    onSubmit: (values) => {
      handleCreateSession(values);
    },
  });

  const handleCreateSession= async(values)=>{
    const newSessionData = {
      name:values.name,
      userID: base?.user?.userID,
      orgID,
      groupID: values?.groupID,
      status:"created",
      payload:{}
    };
    
    try{
      await createSession(newSessionData);
      if(isSuccess){
        toast.success("New session successfully created!");
        formik.resetForm();
      }
    }
    catch(error){
      toast.error(error?.response?.data?.message || "Failed to create session.");
    }
  }


  return (
    <MaterialModal
      open={open}
      handleDialogClose={handleClose}
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            minHeight: "200px",
            minWidth: "500px",
            ".MuiDialogContent-root": {
              overflow: "auto",
            },
          },
        },
      }}
      title="Create Session"
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack direction="column" justifyContent="space-around" gap={2}>
          {/* Session Name */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Session Name</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <TextField
                name="name"
                id="outlined-basic"
                variant="outlined"
                size="small"
                fullWidth
                sx={inputCommonStyle}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.submitCount > 0 && Boolean(formik.errors.name)}
                helperText={formik.submitCount > 0 && formik.errors.name}
              />
            </Box>
          </Stack>

          {/* Select Cohort */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Select Cohort</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
            <FormControl
              size="small"
              variant="outlined"
              sx={{ minWidth: 270, ...inputCommonStyle }}
              error={formik.submitCount > 0 && !formik.values.groupID}
            >
              <Select
                name="groupID"
                value={cohort}
                onChange={(e) => {
                  const value = e.target.value;
                  setCohort(value); // local state
                  formik.setFieldValue("groupID", value); // Formik state
                }}
                onBlur={formik.handleBlur}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select Cohort</em>
                </MenuItem>
                {cohortList?.map((cohort, index) => (
                  <MenuItem key={index} value={cohort?.groupID}>
                    {cohort?.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formik.touched.groupID && formik.errors.groupID}
              </FormHelperText>
            </FormControl>
            </Box>
          </Stack>
        </Stack>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isPending}
            endIcon={isPending ? <CircularProgress size={16} /> : null}
          >
            create
          </Button>
        </Box>
      </form>
    </MaterialModal>
  );
};

export default CreateSessionModal;
